import { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Sun, Moon, Menu, X, ChevronDown } from 'lucide-react'
import { trackEvent } from '../../analytics'
import { SafeImage } from '../ui/SafeImage'
import { useTheme } from '../../context/ThemeContext'
import { serviceSlugs } from '../../data/servicePages'

const mainNav = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Case Studies', path: '/case-study' },
  { label: 'Contact', path: '/contact' },
]

export function Navbar() {
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const servicesTriggerRef = useRef<HTMLDivElement>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40)
      setServicesOpen(false)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useLayoutEffect(() => {
    if (!servicesOpen || !servicesTriggerRef.current) return
    const rect = servicesTriggerRef.current.getBoundingClientRect()
    setDropdownPosition({ top: rect.bottom + 8, left: rect.left })
  }, [servicesOpen])

  const openServices = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    setServicesOpen(true)
  }
  const closeServices = () => {
    closeTimeoutRef.current = setTimeout(() => setServicesOpen(false), 120)
  }
  const cancelCloseServices = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    setMobileOpen(false)
    setServicesOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => { document.body.style.overflow = '' }
  }, [mobileOpen])

  const isActive = (path: string) => location.pathname === path
  const isNavActive = (path: string) => {
    if (path === '/case-study') {
      return location.pathname === '/case-study' || location.pathname.startsWith('/case-study/')
    }
    return location.pathname === path
  }
  const logoSrc = theme === 'light' ? '/logo-light.png' : '/logo-dark.png'

  return (
    <motion.header
      initial={{ y: -16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.35 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass border-b border-white/10 dark:border-white/5 shadow-card' : 'bg-transparent'
      }`}
    >
      <nav className="container-wide">
        <div className="flex items-center justify-between min-h-[72px] sm:min-h-[80px] py-3 sm:py-4">
          <Link to="/" className="flex items-center shrink-0">
            <SafeImage
              src={logoSrc}
              alt="DigiSutra – Best Digital Marketing Agency in Surat"
              label="DigiSutra"
              tone="brand"
              width={160}
              height={56}
              className="h-10 w-auto min-w-[7rem] object-contain sm:h-12 lg:h-14"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${isActive('/') ? 'text-primary dark:text-accent bg-primary/5 dark:bg-accent/10' : 'text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-accent hover:bg-slate-100 dark:hover:bg-slate-800'}`}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${isActive('/about') ? 'text-primary dark:text-accent bg-primary/5 dark:bg-accent/10' : 'text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-accent hover:bg-slate-100 dark:hover:bg-slate-800'}`}
            >
              About
            </Link>
            <div
              ref={servicesTriggerRef}
              className="relative"
              onMouseEnter={openServices}
              onMouseLeave={closeServices}
            >
              <Link
                to="/services"
                className={`flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${location.pathname.startsWith('/services') ? 'text-primary dark:text-accent bg-primary/5 dark:bg-accent/10' : 'text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-accent hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              >
                Services
                <ChevronDown size={16} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
              </Link>
              {servicesOpen &&
                createPortal(
                  <AnimatePresence>
                    <motion.div
                      key="services-dropdown"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.15 }}
                      className="fixed z-[60] pt-2"
                      style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                      onMouseEnter={cancelCloseServices}
                      onMouseLeave={closeServices}
                    >
                      <div className="rounded-2xl glass py-2 min-w-[240px] shadow-card">
                        <Link
                          to="/services"
                          className="block px-4 py-3 text-sm font-medium text-primary dark:text-accent hover:bg-primary/5 dark:hover:bg-accent/10 rounded-lg mx-1.5 transition-colors mb-1"
                        >
                          View All Services
                        </Link>
                        <div className="h-px bg-slate-200 dark:bg-slate-700/50 mx-3 mb-1" />
                        {serviceSlugs.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            className="block px-4 py-3 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-primary dark:hover:text-accent rounded-lg mx-1.5 transition-colors"
                          >
                            {s.title}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  </AnimatePresence>,
                  document.body
                )}
            </div>
            <Link
              to="/case-study"
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${location.pathname === '/case-study' || location.pathname.startsWith('/case-study/') ? 'text-primary dark:text-accent bg-primary/5 dark:bg-accent/10' : 'text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-accent hover:bg-slate-100 dark:hover:bg-slate-800'}`}
            >
              Case Studies
            </Link>
            <Link
              to="/contact"
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${isActive('/contact') ? 'text-primary dark:text-accent bg-primary/5 dark:bg-accent/10' : 'text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-accent hover:bg-slate-100 dark:hover:bg-slate-800'}`}
            >
              Contact
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2.5 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center lg:min-w-0 lg:min-h-0"
              aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>
            <Link
              to="/contact"
              className="hidden lg:inline-flex btn-primary text-sm px-5 py-2.5"
              onClick={() => trackEvent('cta_click', { cta_name: 'book_strategy_call', from: 'navbar_desktop' })}
            >
              Book Strategy Call
            </Link>
            <button
              type="button"
              className="lg:hidden relative z-[60] p-2.5 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 min-w-[44px] min-h-[44px] flex items-center justify-center [touch-action:manipulation]"
              onClick={(e) => { e.stopPropagation(); setMobileOpen((o) => !o); }}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mobile-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-slate-900/20 dark:bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
        )}
        {mobileOpen && (
          <motion.div
            key="mobile-panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-sm glass-strong shadow-2xl lg:hidden flex flex-col border-l border-white/10"
          >
            <div className="p-4 sm:p-6 flex flex-col gap-1 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <SafeImage
                  src={logoSrc}
                  alt="DigiSutra – Best Digital Marketing Agency in Surat"
                  label="DigiSutra"
                  tone="brand"
                  width={140}
                  height={44}
                  className="h-11 w-auto min-w-[6rem] object-contain"
                />
                <button type="button" onClick={() => setMobileOpen(false)} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
                  <X size={24} />
                </button>
              </div>
              {mainNav.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3.5 rounded-xl font-medium text-base ${isNavActive(link.path) ? 'text-primary dark:text-accent bg-primary/10 dark:bg-accent/10' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                >
                  {link.label}
                </Link>
              ))}
              <p className="px-4 pt-4 pb-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Services
              </p>
              <Link
                to="/services"
                onClick={() => setMobileOpen(false)}
                className="px-4 py-3 rounded-xl text-primary dark:text-accent font-medium hover:bg-slate-100 dark:hover:bg-slate-800 text-sm"
              >
                View All Services
              </Link>
              {serviceSlugs.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 text-sm"
                >
                  {s.title}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => { setMobileOpen(false); trackEvent('cta_click', { cta_name: 'book_strategy_call', from: 'navbar_mobile' }) }}
                className="mt-6 btn-primary w-full justify-center"
              >
                Book Strategy Call
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  )
}
